import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { getApiBaseUrl } from '@/lib/apiConfig';

// Use bridgeapi for all location endpoints (legacy / future MySQL mode)
const getLocationsApiUrl = () => `${getApiBaseUrl()}/locations.php`;
const getLocationsAdminApiUrl = () => `${getApiBaseUrl()}/locations-admin.php`;

export interface LocationCountry {
  id: string | number;
  title: string;
  code?: string;
}

export interface LocationState {
  id: string | number;
  title: string;
  is_state_popular?: string;
  country_id?: string | number;
}

export interface LocationCity {
  id: string | number;
  title: string;
  state_id: string | number;
  state_name?: string;
  is_city_popular?: string;
}

export interface LocationArea {
  id: string | number;
  title: string;
  city_id: string | number;
  pincode?: string;
}

// ---- Public hooks ----------------------------------------------------------

export function useCountries() {
  return useQuery<LocationCountry[]>({
    queryKey: ['location-countries'],
    queryFn: async () => {
      return [{ id: 1, title: 'India', code: 'IN' }];
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useStates() {
  return useQuery({
    queryKey: ['states'],
    queryFn: async () => {
      return api.getStates();
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useCities(stateId?: number | string) {
  return useQuery({
    queryKey: ['cities', stateId],
    queryFn: async () => {
      return api.getCities(stateId as number);
    },
    enabled: stateId !== undefined,
    staleTime: 30 * 60 * 1000,
  });
}

export function usePopularCities() {
  return useQuery({
    queryKey: ['popular-cities'],
    queryFn: async () => {
      return api.getPopularCities();
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function usePopularCityCategories(limit = 12) {
  return useQuery({
    queryKey: ['popular-city-categories', limit],
    queryFn: async () => {
      const res = await api.getPopularCityCategories(limit);
      return res.data || [];
    },
    staleTime: 30 * 60 * 1000,
  });
}

// Admin/Direct fetch hooks for centralized location selector
export function useLocationStates() {
  return useQuery<LocationState[]>({
    queryKey: ['location-states'],
    queryFn: async () => {
      const res = await fetch(`${getLocationsApiUrl()}?type=states`);
      if (!res.ok) throw new Error('Failed to fetch states');
      const data = await res.json();
      return data.data || [];
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function useLocationCities(stateId?: number | string) {
  return useQuery<LocationCity[]>({
    queryKey: ['location-cities', stateId],
    queryFn: async () => {
      if (!stateId) return [];
      const res = await fetch(`${getLocationsApiUrl()}?type=cities&state_id=${stateId}`);
      if (!res.ok) throw new Error('Failed to fetch cities');
      const data = await res.json();
      return data.data || [];
    },
    enabled: !!stateId,
    staleTime: 30 * 60 * 1000,
  });
}

export function useLocationAreas(cityId?: number | string) {
  return useQuery<LocationArea[]>({
    queryKey: ['location-areas', cityId],
    queryFn: async () => {
      if (!cityId) return [];
      const res = await fetch(`${getLocationsApiUrl()}?type=neighborhoods&city_id=${cityId}`);
      if (!res.ok) throw new Error('Failed to fetch areas');
      const data = await res.json();
      return data.data || [];
    },
    enabled: !!cityId,
    staleTime: 30 * 60 * 1000,
  });
}

export function useAllCities() {
  return useQuery<LocationCity[]>({
    queryKey: ['all-cities'],
    queryFn: async () => {
      const res = await fetch(`${getLocationsAdminApiUrl()}?action=list&type=cities&limit=500`);
      if (!res.ok) throw new Error('Failed to fetch all cities');
      const data = await res.json();
      return data.data || [];
    },
    staleTime: 30 * 60 * 1000,
  });
}